import React from 'react';
import { X, Globe, Laptop } from 'lucide-react';
import { useClickOutside } from '../../hooks/useClickOutside';
import useEscapeKey from '../../hooks/useEscapeKey';
import { motion } from 'framer-motion';

interface PersistenceConflictDialogProps {
  isOpen: boolean;
  onLoadBackend: () => void;
  onLoadPersisted: () => void;
  onClose: () => void;
  editorType: 'playbook' | 'schema' | 'integration' | 'contract';
  itemName?: string;
  persistedTimestamp?: number;
  backendTimestamp?: number;
}

const PersistenceConflictDialog: React.FC<PersistenceConflictDialogProps> = ({
  isOpen,
  onLoadBackend,
  onLoadPersisted,
  onClose,
  editorType,
  itemName = 'item',
  persistedTimestamp,
  backendTimestamp,
}) => {
  const formatTimestamp = (timestamp?: number) => {
    if (!timestamp) return 'Unknown time';
    return new Date(timestamp).toLocaleString();
  };

  const getTimeDifference = () => {
    if (!persistedTimestamp || !backendTimestamp) return '';
    
    const diff = Math.abs(persistedTimestamp - backendTimestamp);
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days} day${days > 1 ? 's' : ''} apart`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} apart`;
    if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} apart`;
    return 'Less than a minute apart';
  };

  const handleClose = () => {
    onClose();
  };

  const dialogRef = useClickOutside<HTMLDivElement>(handleClose);
  useEscapeKey(handleClose);

  if (!isOpen) return null;

  return (
    <motion.div
      className="fixed inset-0 bg-gray-950/50 dark:bg-gray-950/70 flex items-center justify-center z-[200]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        ref={dialogRef}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-[600px] mx-4 border border-gray-200 dark:border-gray-700"
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
      >
        {/* Header */}
        <div className="px-6 pt-5 rounded-t-lg">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Unsaved Changes Detected
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-300 dark:text-gray-500 dark:hover:text-gray-400"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="space-y-4">
            <p className="text-gray-700 dark:text-gray-300">
              You have unsaved changes for this {editorType} ({itemName}) from your last session. 
              These changes conflict with the version saved on the server.
            </p>
            
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-3">
              <div className="grid grid-cols-2 gap-4 text-sm">
                 <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-1 flex items-center gap-2">
                    <Globe className="h-4 w-4 text-green-600 dark:text-green-400" />
                    Saved Version
                  </h4>
                  <p className="text-gray-600 dark:text-gray-400">
                    Last saved: {formatTimestamp(backendTimestamp)}
                  </p>
                  <p className="text-green-600 dark:text-green-400 font-medium">
                    Latest saved state
                  </p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-1 flex items-center gap-2">
                    <Laptop className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    Unsaved Changes
                  </h4>
                  <p className="text-gray-600 dark:text-gray-400">
                    Last modified: {formatTimestamp(persistedTimestamp)}
                  </p>
                  <p className="text-blue-600 dark:text-blue-400 font-medium">
                    Contains your local edits
                  </p>
                </div>
              </div>
              
              {persistedTimestamp && backendTimestamp && (
                <div className="text-center text-sm text-gray-500 dark:text-gray-400 border-t pt-2">
                  Versions are {getTimeDifference()}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 rounded-b-lg border-t border-gray-100 dark:border-gray-800">
          <div className="flex justify-end gap-3">
            <button
              onClick={onLoadBackend}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-200"
            >
              Discard & Load Saved
            </button>
            <button
              onClick={onLoadPersisted}
              className="px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              Load Unsaved Changes
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default PersistenceConflictDialog;
